import React, { useState, useEffect } from "react";
import {
  Box,
  CircularProgress,
  Typography,
  Grid,
  Card,
  CardContent,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Popover,
  tableCellClasses,
  Badge,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "../../globalStyles/icons/editIcon";
import ViewIcon from "../../globalStyles/icons/vieewIcon";
import DeleteIcon from "../../globalStyles/icons/deleteIcon";
import CustomTypography from "../../globalStyles/fonts";
import { isMobile } from "../../globalStyles/mobile";
import DeleteConfirmationModal from "../deleteConfirmationModal";
import ProvinceDetailsModal from "../provinvce-details-modal";
import { provinceService } from "../../utils/services/province.service";
import { useTheme } from "@mui/styles";
import SortingIcon from "../../globalStyles/icons/sorting-icon";
import FilterModal from "../filterModal";

const StyledTableCell = styled(TableCell)({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "var(--table-header-bg)",
    border: "none",
    padding: 4,
  },
  [`&.${tableCellClasses.body}`]: { fontSize: 12, padding: 6 },
});

const StyledTableRow = styled(TableRow)({
  "&:nth-of-type(odd)": {
    backgroundColor: "var(--row-bg-odd)",
    border: "1px solid var(--border-color)",
  },
  "&:nth-of-type(even)": {
    backgroundColor: "var(--row-bg-even)",
    border: "1px solid var(--border-color)",
  },
  "&:lastChild td, &:lastChild th": { border: 0 },
});

const ProvinceTableCell = ({
  province,
  handleViewClick,
  handleEditClick,
  handleDeleteClick,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const handlePopoverOpen = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };
  const handlePopoverClose = () => {
    setAnchorEl(null);
  };
  const open = Boolean(anchorEl);

  const handleActionClick = (action) => {
    handlePopoverClose();
    switch (action) {
      case "view":
        handleViewClick(province);
        break;
      case "edit":
        handleEditClick(province);
        break;
      case "delete":
        handleDeleteClick(province);
        break;
      default:
        break;
    }
  };

  return (
    <Box>
      <IconButton
        onClick={handlePopoverOpen}
        sx={{ color: open ? "primary.main" : "inherit" }}
      >
        <MoreVertIcon />
      </IconButton>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handlePopoverClose}
        anchorOrigin={{ vertical: "top", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", padding: "10px" }}>
          <Button
            onClick={() => handleActionClick("edit")}
            style={{ textTransform: "none" }}
          >
            <Box
              display="flex"
              alignItems="center"
              justifyContent="flex-start"
              width="100%"
              gap={1}
            >
              <EditIcon />{" "}
              <Typography
                fontFamily={"poppins"}
                fontSize={"16px"}
                color={"var(--text-tertiary)"}
                fontWeight={500}
              >
                Edit
              </Typography>
            </Box>
          </Button>
          <Button
            onClick={() => handleActionClick("view")}
            style={{ textTransform: "none" }}
          >
            <Box
              display="flex"
              alignItems="center"
              justifyContent="flex-start"
              width="100%"
              gap={1}
            >
              <ViewIcon />
              <Typography
                fontFamily={"poppins"}
                fontSize={"16px"}
                color={"var(--text-tertiary)"}
                fontWeight={500}
              >
                View
              </Typography>
            </Box>
          </Button>
          <Button
            onClick={() => handleActionClick("delete")}
            style={{ textTransform: "none" }}
          >
            <Box
              display="flex"
              alignItems="center"
              justifyContent="flex-start"
              width="100%"
              gap={1}
            >
              <DeleteIcon />{" "}
              <Typography
                fontFamily={"poppins"}
                fontSize={"16px"}
                color={"var(--text-tertiary)"}
                fontWeight={500}
              >
                Delete
              </Typography>
            </Box>
          </Button>
        </Box>
      </Popover>
    </Box>
  );
};

const ProvinceList = ({
  data,
  onEditData,
  onDeleteData,
  viewMode,
  selectedProvince,
  setSelectedProvince,
  currentPage,
  itemsPerPage,
  filterData,
  onChangeFilter,
}) => {
  const [openConfirmation, setOpenConfirmation] = useState(false);
  const [loading, setLoading] = useState(false);
  const [openViewModal, setOpenViewModal] = useState(false);
  const [selectedProvinceData, setSelectedProvinceData] = useState([]);
  const [openFilterModal, setOpenFilterModal] = useState(false);
  const [anchorElFilter, setAnchorElFilter] = useState(null);
  const [selectedfilterData, setSelectedFilterData] = useState({
    key: "",
    filter: [],
    sort: 0,
    id: "_id",
    displayKey: "name",
    fetchApi: () => {},
    loadMoreButton: false,
    options: [],
    searchKey: "",
  });
  const theme = useTheme();
  const defaultColor = "#D13438";
  const primary_color = theme.palette?.primary?.main || defaultColor;
  const startIndex = currentPage * itemsPerPage;

  const handleDeleteClick = (prov) => {
    setSelectedProvince(prov);
    setOpenConfirmation(true);
  };

  const confirmDelete = async () => {
    try {
      setLoading(true);
      await provinceService.delete(selectedProvince._id);
      onDeleteData && onDeleteData();
      setOpenConfirmation(false);
    } catch (error) {
      console.error("Delete failed:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewClick = (prov) => {
    setSelectedProvince(prov);
    setOpenViewModal(true);
  };

  useEffect(() => {
    setSelectedProvinceData(
      data?.map((x) => ({
        ...x,
        action: (
          <ProvinceTableCell
            province={x}
            handleViewClick={handleViewClick}
            handleEditClick={onEditData}
            handleDeleteClick={handleDeleteClick}
          />
        ),
      })),
    );
  }, [data]);

  const handleOpenFilter = (event, filterKey) => {
    setAnchorElFilter(event.currentTarget);
    setOpenFilterModal(true);
    setSelectedFilterData(filterData.columns.find((x) => x.key == filterKey));
  };

  const handleSaveFilter = (
    selectedOptions,
    filterKey,
    sortOrder,
    searchKey,
  ) => {
    onChangeFilter({
      key: filterKey,
      filter: selectedOptions,
      sort: sortOrder,
      searchKey: searchKey,
    });
    handleCloseFilter();
  };

  const handleCloseFilter = () => {
    setAnchorElFilter(null);
    setOpenFilterModal(false);
  };

  const renderGridView = () => (
    <Grid
      container
      spacing={2}
      sx={{ marginTop: 1, maxHeight: "100%", overflow: "auto" }}
    >
      {selectedProvinceData?.map((item, index) => (
        <Grid item xs={12} sm={6} md={4} key={index} sx={{ paddingBottom: 1 }}>
          <Card
            sx={{
              border: "1px solid var(--border-color)",
              borderRadius: 1,
              boxShadow: 3,
              height: "100%",
              position: "relative",
            }}
          >
            <CardContent>
              <Box display="flex" justifyContent="space-between">
                <Typography
                  fontFamily="Poppins"
                  fontWeight={500}
                  color="var(--text-tertiary)"
                >
                  {startIndex + index + 1}
                </Typography>
                <Box position="absolute" top={10} right={10}>
                  {item.action}
                </Box>
              </Box>

              <Grid container spacing={1} mt={2}>
                <Grid item xs={5}>
                  <Typography
                    fontFamily="Poppins"
                    fontWeight={500}
                    color="var(--text-tertiary)"
                    textAlign={"start"}
                  >
                    Name
                  </Typography>
                </Grid>
                <Grid item xs={7}>
                  <Tooltip title={item.name}>
                    <Typography
                      fontFamily="Poppins"
                      fontWeight={500}
                      color="var(--text-tertiary)"
                      noWrap
                      textAlign={"start"}
                    >
                      : {item.name}
                    </Typography>
                  </Tooltip>
                </Grid>

                <Grid item xs={5}>
                  <Typography
                    fontFamily="Poppins"
                    fontWeight={500}
                    color="var(--text-tertiary)"
                    textAlign={"start"}
                  >
                    Code
                  </Typography>
                </Grid>
                <Grid item xs={7}>
                  <Tooltip title={item.code}>
                    <Typography
                      fontFamily="Poppins"
                      fontWeight={500}
                      color="var(--text-tertiary)"
                      noWrap
                      textAlign={"start"}
                    >
                      : {item.code}
                    </Typography>
                  </Tooltip>
                </Grid>

                <Grid item xs={5}>
                  <Typography
                    fontFamily="Poppins"
                    fontWeight={500}
                    color="var(--text-tertiary)"
                    textAlign={"start"}
                  >
                    Country
                  </Typography>
                </Grid>
                <Grid item xs={7}>
                  <Tooltip title={item?.country?.name}>
                    <Typography
                      fontFamily="Poppins"
                      fontWeight={500}
                      color="var(--text-tertiary)"
                      noWrap
                      textAlign={"start"}
                    >
                      : {item?.country?.name}
                    </Typography>
                  </Tooltip>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );

  const renderListView = () => (
    <TableContainer
      sx={{ overflowY: "auto", maxHeight: "100%", paddingX: 0.4 }}
    >
      <Table stickyHeader sx={{ border: "1px solid var(--border-color)" }}>
        <TableHead>
          <StyledTableRow>
            <StyledTableCell>
              <Typography
                fontSize={14}
                fontWeight={500}
                fontFamily="Poppins"
                color="var(--text-tertiary)"
              >
                SL NO
              </Typography>
            </StyledTableCell>
            <StyledTableCell>
              <div
                style={{
                  display: "flex",
                  justifyContent: "start",
                  alignItems: "center",
                }}
              >
                <Typography
                  fontSize={14}
                  fontWeight={500}
                  fontFamily="Poppins"
                  color="var(--text-tertiary)"
                >
                  NAME
                </Typography>
                <Badge
                  color="primary"
                  variant="dot"
                  invisible={
                    !filterData?.columns?.find(
                      (item) => item.key.toLowerCase() === "name",
                    )?.filter?.length
                  }
                  overlap="circular"
                >
                  <IconButton
                    aria-label="filter"
                    size="small"
                    onClick={(event) => handleOpenFilter(event, "name")}
                  >
                    <SortingIcon fill={primary_color || "#DC585C"} />
                  </IconButton>
                </Badge>
              </div>
            </StyledTableCell>
            <StyledTableCell>
              <div
                style={{
                  display: "flex",
                  justifyContent: "start",
                  alignItems: "center",
                }}
              >
                <Typography
                  fontSize={14}
                  fontWeight={500}
                  fontFamily="Poppins"
                  color="var(--text-tertiary)"
                >
                  CODE
                </Typography>
                <Badge
                  color="primary"
                  variant="dot"
                  invisible={
                    !filterData?.columns?.find(
                      (item) => item.key.toLowerCase() === "code",
                    )?.filter?.length
                  }
                  overlap="circular"
                >
                  <IconButton
                    aria-label="filter"
                    size="small"
                    onClick={(event) => handleOpenFilter(event, "code")}
                  >
                    <SortingIcon fill={primary_color || "#DC585C"} />
                  </IconButton>
                </Badge>
              </div>
            </StyledTableCell>
            <StyledTableCell>
              <div
                style={{
                  display: "flex",
                  justifyContent: "start",
                  alignItems: "center",
                }}
              >
                <Typography
                  fontSize={14}
                  fontWeight={500}
                  fontFamily="Poppins"
                  color="var(--text-tertiary)"
                >
                  COUNTRY
                </Typography>
                <Badge
                  color="primary"
                  variant="dot"
                  invisible={
                    !filterData?.columns?.find(
                      (item) => item.key.toLowerCase() === "country",
                    )?.filter?.length
                  }
                  overlap="circular"
                >
                  <IconButton
                    aria-label="filter"
                    size="small"
                    onClick={(event) => handleOpenFilter(event, "country")}
                  >
                    <SortingIcon fill={primary_color || "#DC585C"} />
                  </IconButton>
                </Badge>
              </div>
            </StyledTableCell>
            <StyledTableCell />
          </StyledTableRow>
        </TableHead>
        <TableBody>
          {selectedProvinceData?.map((c, i) => (
            <StyledTableRow key={c._id}>
              <StyledTableCell>
                <Typography
                  fontSize={12}
                  fontFamily="Poppins"
                  color="var(--text-secondary)"
                >
                  {startIndex + i + 1}
                </Typography>
              </StyledTableCell>
              <StyledTableCell>
                <Tooltip title={c.name}>
                  <Typography
                    fontSize={12}
                    fontFamily="Poppins"
                    color="var(--text-secondary)"
                    noWrap
                    sx={{
                      maxWidth: 100,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {c.name}
                  </Typography>
                </Tooltip>
              </StyledTableCell>
              <StyledTableCell>
                <Typography
                  fontSize={12}
                  fontFamily="Poppins"
                  color="var(--text-secondary)"
                  noWrap
                >
                  {c.code}
                </Typography>
              </StyledTableCell>
              <StyledTableCell>
                <Typography
                  fontSize={12}
                  fontFamily="Poppins"
                  color="var(--text-secondary)"
                  noWrap
                >
                  {c.country?.name}
                </Typography>
              </StyledTableCell>
              <StyledTableCell sx={{ width: "3%", textAlign: "center" }}>
                {c.action}
              </StyledTableCell>
            </StyledTableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Box sx={{ width: "100%" }}>
      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            zIndex: 9999,
            color: "white",
          }}
        >
          <CircularProgress color="inherit" />
        </Box>
      ) : (
        <Box
          sx={{
            overflowY: "scroll",
            maxHeight: isMobile() ? "60vh" : `calc(100vh - 250px)`,
          }}
        >
          {viewMode === "grid" ? renderGridView() : renderListView()}
        </Box>
      )}
      <DeleteConfirmationModal
        title={`Are you sure you want to delete ${selectedProvince?.name} ?`}
        open={openConfirmation}
        handleClose={() => setOpenConfirmation(false)}
        handleDelete={confirmDelete}
      />
      <ProvinceDetailsModal
        province={selectedProvince}
        open={openViewModal}
        onClose={() => setOpenViewModal(false)}
      />
      <Popover
        open={Boolean(anchorElFilter)}
        anchorEl={anchorElFilter}
        onClose={handleCloseFilter}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        disableScrollLock={true}
      >
        <FilterModal
          open={openFilterModal}
          onClose={handleCloseFilter}
          options={selectedfilterData?.options}
          selectedValues={selectedfilterData?.filter}
          onSave={handleSaveFilter}
          currentSortOrder={selectedfilterData?.sort}
          filterKey={selectedfilterData?.key}
          hasMore={selectedfilterData?.loadMoreButton}
          displayKey={selectedfilterData?.displayKey}
          id={selectedfilterData?.id}
          fetchApi={selectedfilterData?.fetchApi}
          searchKey={selectedfilterData?.searchKey}
        />
      </Popover>
    </Box>
  );
};

export default ProvinceList;
